import { isFinite } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

import { getFilterBy } from 'components/DataTable/paging';
import { BladeSide } from 'horizon/components/Damages/BladeDamagePlot/BladeDamagePlotContainer';
import {
  type AtlasGqlFilterBy,
  type AtlasGqlTableView,
  useBladeCardSummaryStatsQuery,
} from 'types/atlas-graphql';

import { MAX_BLADE_CARD_POSITIONS } from './constants';

const SUMMARY_ITEM_LIMIT = 1;
const LOAD_ALL_PAGE_SIZE = 1500;

type ViewAssetFilter = { key: string; value: [string] };

export type BladeCardViewFilters = {
  asset?: ViewAssetFilter[];
  'Blade Side'?: string[];
  Distance?: [[number, number]];
  Chord?: [[number, number]];
  [key: string]: unknown;
};

const getAssetValue = (filters: BladeCardViewFilters, key: string): string | undefined =>
  filters.asset?.find(f => f.key === key)?.value?.[0];

const parseBladeSide = (s?: string): BladeSide | undefined => {
  const allowed: BladeSide[] = [
    BladeSide.LeadingEdge,
    BladeSide.PressureSide,
    BladeSide.SuctionSide,
    BladeSide.TrailingEdge,
  ];
  return allowed.find(side => side === s);
};

export type BladeCardDamagePosition = {
  id: string;
  distance: number;
  chord?: number;
  severity?: number;
  isCritical: boolean;
};

export type BladeCardRepairCosts = {
  damagesWithCostCount: number;
  totalRepairCost: number;
  totalRepairCostExclDowntime: number;
  totalRepairCostWithStandbyExclDowntime: number;
};

export type BladeCardStats = {
  make?: string;
  model?: string;
  bladeSide?: BladeSide;
  distance?: [number, number];
  chord?: [number, number];
  totalCount: number;
  loadedCount: number;
  criticalCount: number | null;
  uniqueBlades: number | null;
  uniqueTurbines: number | null;
  totalRepairCost: number | null;
  repairCosts: BladeCardRepairCosts | null;
  positions: BladeCardDamagePosition[];
  isLoading: boolean;
  isLoadingPositions: boolean;
  isLoadingAllPositions: boolean;
  positionLoadError?: unknown;
  error?: unknown;
  canRenderDiagram: boolean;
};

type UseBladeCardStatsOptions = {
  skip?: boolean;
  withPositions?: boolean;
  loadAllPositions?: boolean;
};

export const useBladeCardStats = (
  view: AtlasGqlTableView,
  { skip = false, withPositions = false, loadAllPositions = false }: UseBladeCardStatsOptions = {}
): BladeCardStats => {
  const filters = view.filters as BladeCardViewFilters;
  const make = getAssetValue(filters, 'componentMake');
  const model = getAssetValue(filters, 'componentModel');
  const bladeSide = parseBladeSide(filters['Blade Side']?.[0]);
  const distance = filters.Distance?.[0];
  const chord = filters.Chord?.[0];

  const filterBy = useMemo(
    () => getFilterBy({ filters, columns: [] }).filter(Boolean) as AtlasGqlFilterBy[],
    [filters]
  );

  const { data, loading, error, fetchMore } = useBladeCardSummaryStatsQuery({
    variables: {
      input: {},
      filterBy,
      // Summary values come from `metrics`, so non-diagram consumers do not need a
      // Damage payload. Diagram consumers receive a bounded sample of positions.
      limit: withPositions ? MAX_BLADE_CARD_POSITIONS : SUMMARY_ITEM_LIMIT,
      offset: 0,
      withPositions,
    },
    skip,
  });

  const rawItems = data?.getHorizonDamages?.items;
  const items = rawItems ?? [];
  const totalCount = data?.getHorizonDamages?.totalCount ?? 0;
  const metrics = data?.getHorizonDamages?.metrics;
  const repairCosts = metrics?.repairCost ?? null;
  const requestedOffset = useRef<number | null>(null);
  const [isLoadingAllPositions, setIsLoadingAllPositions] = useState(false);
  const [positionLoadError, setPositionLoadError] = useState<unknown>();

  useEffect(() => {
    requestedOffset.current = null;
    setIsLoadingAllPositions(false);
    setPositionLoadError(undefined);
  }, [view.id, loadAllPositions]);

  useEffect(() => {
    const offset = rawItems?.length ?? 0;
    if (
      skip ||
      !withPositions ||
      !loadAllPositions ||
      loading ||
      error ||
      positionLoadError ||
      !rawItems ||
      offset >= totalCount ||
      requestedOffset.current === offset
    ) {
      return;
    }

    requestedOffset.current = offset;
    setIsLoadingAllPositions(true);
    void fetchMore({
      variables: { limit: LOAD_ALL_PAGE_SIZE, offset },
      updateQuery: (previousData, { fetchMoreResult }) => ({
        getHorizonDamages: {
          ...previousData.getHorizonDamages,
          ...fetchMoreResult.getHorizonDamages,
          items: [
            ...(previousData.getHorizonDamages?.items ?? []),
            ...(fetchMoreResult.getHorizonDamages?.items ?? []),
          ],
          totalCount: previousData.getHorizonDamages?.totalCount,
          metrics: previousData.getHorizonDamages?.metrics,
        },
      }),
    })
      .then(result => {
        if (!(result.data?.getHorizonDamages?.items?.length ?? 0)) {
          setPositionLoadError(new Error('No additional Damage positions were returned'));
        }
      })
      .catch(setPositionLoadError)
      .finally(() => setIsLoadingAllPositions(false));
  }, [
    error,
    fetchMore,
    loadAllPositions,
    loading,
    positionLoadError,
    rawItems,
    skip,
    totalCount,
    withPositions,
  ]);

  const positions = useMemo(
    () =>
      (rawItems ?? [])
        .slice(0, loadAllPositions ? undefined : MAX_BLADE_CARD_POSITIONS)
        .reduce<BladeCardDamagePosition[]>((acc, item) => {
          const attrs = item?.primaryObservationGroupAttrs;
          const itemDistance = attrs?.Distance;
          if (!item?.id || !isFinite(itemDistance)) {
            return acc;
          }
          const itemChord = attrs?.Chord;
          const itemSeverity = attrs?.Severity;
          acc.push({
            id: item.id,
            distance: itemDistance,
            chord: isFinite(itemChord) ? itemChord : undefined,
            severity: isFinite(itemSeverity) ? itemSeverity : undefined,
            isCritical: item.primaryObservationGroupCritical === true,
          });
          return acc;
        }, []),
    [loadAllPositions, rawItems]
  );

  return {
    make,
    model,
    bladeSide,
    distance,
    chord,
    totalCount,
    loadedCount: items.length,
    criticalCount: metrics?.criticalCount ?? null,
    uniqueBlades: metrics?.assetCount ?? null,
    uniqueTurbines: metrics?.turbineCount ?? null,
    totalRepairCost: repairCosts?.totalRepairCost ?? null,
    repairCosts,
    positions,
    isLoading: skip || loading || (!data && !error),
    isLoadingPositions: withPositions && loading && !rawItems,
    isLoadingAllPositions,
    positionLoadError,
    error,
    canRenderDiagram: !!make && !!model && !!bladeSide && !!distance,
  };
};
